import type { ReactNode } from 'react'
import { cn } from '../../../../lib/utils'
import type { SectionTone } from './deriveSectionStatus'

/** Liseré colours (4px left border), _design Round 4 §Style/tokens. */
const TONE_HEX: Record<SectionTone, string> = {
  gris: '#cbd5e1',
  ambre: '#f59e0b',
  rouge: '#ef4444',
  vert: '#10b981',
}

/** Status text colour — discreet text only, never a filled pill. */
const STATUS_TEXT: Record<SectionTone, string> = {
  gris: 'text-muted-foreground',
  ambre: 'text-amber-600',
  rouge: 'text-red-600',
  vert: 'text-emerald-600',
}

interface SectionCardProps {
  title: string
  /** Optional badge rendered right after the title (SOLY-FINITION-001 nature pill). */
  titleBadge?: ReactNode
  tone: SectionTone
  /** Derived user-first label, or null for the calm/factual state (renders no status node). */
  statusLabel: string | null
  count?: number
  children: ReactNode
  testId: string
  statusTestId?: string
}

/**
 * Style B "liseré & épuré" section card (SOLY-PLAN-001 MS-23, AC2): white card with a 4px coloured
 * left border carrying the state, and the status shown as discreet text (no filled background, no
 * coloured pill). Reusable for Commandes · Métré · Pose. Renders no status node in the calm state.
 */
export function SectionCard({ title, titleBadge, tone, statusLabel, count, children, testId, statusTestId }: SectionCardProps) {
  return (
    <section
      data-testid={testId}
      className="rounded-lg border bg-white p-3"
      style={{ borderLeftWidth: '4px', borderLeftColor: TONE_HEX[tone] }}
    >
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <h4 className="inline-flex items-center gap-1.5 text-sm font-semibold text-foreground">{title}{titleBadge}</h4>
        {statusLabel !== null && (
          <span data-testid={statusTestId} className={cn('inline-flex items-center gap-1.5 text-xs font-medium', STATUS_TEXT[tone])}>
            {/* Small coloured dot so the at-a-glance state competes with the field rows (C1). A dot,
                not a filled pill — keeps the « statut en texte, pas de fond plein » token. */}
            <span className="h-1.5 w-1.5 flex-none rounded-full" style={{ backgroundColor: TONE_HEX[tone] }} aria-hidden />
            {statusLabel}
            {typeof count === 'number' && count > 0 && (
              <span className="font-normal"> · {count} point{count > 1 ? 's' : ''}</span>
            )}
          </span>
        )}
      </div>
      {children}
    </section>
  )
}
